import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BUSINESS_CONFIG } from '@/lib/constants';

interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
  className?: string;
}

const stepLabels = BUSINESS_CONFIG.ONBOARDING.STEPS.map(step => step.label);

export function ProgressIndicator({ currentStep, totalSteps, className }: ProgressIndicatorProps) {
  const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);
  const progressPercentage = currentStep > 1 ? 100 : 0;

  return (
    <div className={cn('w-full max-w-2xl mx-auto', className)}>
      {/* Progress bar with connecting lines */}
      <div className="relative">
        <div
          data-testid="onboarding-progress-track"
          className="absolute top-6 left-8 right-8 h-0.5 overflow-hidden rounded-full bg-night-100"
        >
          <div
            data-testid="onboarding-progress-fill"
            className="h-full rounded-full bg-blueviolet transition-all duration-500 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        {/* Steps */}
        <div className="relative flex items-center justify-between">
          {steps.map((step, index) => {
            const isCompleted = step < currentStep;
            const isCurrent = step === currentStep;
            const isUpcoming = step > currentStep;

            return (
              <div key={step} className="flex flex-col items-center">
                {/* Step circle */}
                <div
                  className={cn(
                    'flex items-center justify-center w-12 h-12 rounded-full border-3 transition-all duration-300 shadow-sm',
                    {
                      // Completed step
                      'bg-avocado border-blueviolet text-blueviolet shadow-lilac-900':
                        isCompleted,
                      // Current step
                      'bg-white border-blueviolet text-blueviolet shadow-lilac-900 ring-4 ring-lilac-900':
                        isCurrent,
                      // Upcoming step
                      'bg-white border-night-300 text-night-300': isUpcoming,
                    }
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <span className="font-body text-sm font-bold">{step}</span>
                  )}
                </div>

                {/* Step label */}
                <div className="mt-3 text-center">
                  <span
                    className={cn('font-body text-xs font-medium transition-colors', {
                      'text-blueviolet': isCompleted || isCurrent,
                      'text-night-500': isUpcoming,
                    })}
                  >
                    {stepLabels[index]}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress percentage */}
      {/* <div className="mt-6 text-center">
        <div className="inline-flex items-center space-x-2">
          <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blush-500 to-tigerlily transition-all duration-500 ease-out"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <span className="font-body text-xs font-medium text-gray-600">
            {Math.round((currentStep / totalSteps) * 100)}%
          </span>
        </div>
      </div> */}
    </div>
  );
}
